import React, { useEffect, useState, useCallback, useRef } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useSnackbar } from 'react-simple-snackbar'
import {
  fetchCorporateClients,
  createCorporateClient,
  updateCorporateClient,
  deleteCorporateClient,
  importCorporateClientsExcel,
} from '../../api/index'
import { useCorporateGuard } from '../../hooks/useRoleGuard'
import { isAdmin, isCorporate } from '../../utils/auth'
import VisibilityOutlinedIcon from '@material-ui/icons/VisibilityOutlined'
import EditOutlinedIcon from '@material-ui/icons/EditOutlined'
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline'
import ConfirmModal from '../ConfirmModal/ConfirmModal'
import BackButton from '../BackButton/BackButton'
import Spinner from '../Spinner/Spinner'
import styles from './CorporateClients.module.css'

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100, 300]
const DEFAULT_LIMIT = 10

const emptyForm = { name: '', visAVis: '', matriculeFiscal: '', address: '', email: '' }

const clientInitial = (name) => (name?.trim()?.charAt(0)?.toUpperCase() || '?')

const CorporateClients = () => {
  const history = useHistory()
  const location = useLocation()
  const [openSnackbar] = useSnackbar()
  const openSnackbarRef = useRef(openSnackbar)
  openSnackbarRef.current = openSnackbar
  useCorporateGuard()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(DEFAULT_LIMIT)
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState(null)
  const [form, setForm] = useState(emptyForm)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [confirmModal, setConfirmModal] = useState(null)
  const [selectedIds, setSelectedIds] = useState([])
  const [importing, setImporting] = useState(false)
  const importInputRef = useRef(null)
  const canManageClients = isAdmin() || isCorporate()

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const { data: result } = await fetchCorporateClients({ search, page, limit })
      setData(result)
    } catch (error) {
      openSnackbarRef.current(error?.response?.data?.message || 'Erreur')
    } finally {
      setLoading(false)
    }
  }, [search, page, limit])

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, page, limit])

  useEffect(() => {
    setPage(1)
  }, [search, limit])

  useEffect(() => {
    setSelectedIds([])
  }, [page, search, limit])

  const openCreate = () => {
    setEditing(null)
    setForm(emptyForm)
    setModalOpen(true)
  }

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    if (params.get('new') === '1' && canManageClients) {
      openCreate()
      history.replace('/corporate/clients')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, canManageClients])

  const openEdit = (client) => {
    setEditing(client)
    setForm({
      name: client.name || '',
      visAVis: client.visAVis || '',
      matriculeFiscal: client.matriculeFiscal || '',
      address: client.address || '',
      email: client.email || '',
    })
    setModalOpen(true)
  }

  const handleSave = async (e) => {
    e.preventDefault()
    if (!form.name.trim()) {
      openSnackbar('Nom requis (identique au nom PDF Sage)')
      return
    }
    setSaving(true)
    try {
      if (editing) {
        await updateCorporateClient(editing._id, form)
        openSnackbar('Client mis à jour')
      } else {
        await createCorporateClient(form)
        openSnackbar('Client créé')
      }
      setModalOpen(false)
      load()
    } catch (error) {
      openSnackbar(error?.response?.data?.message || 'Erreur enregistrement')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!confirmModal?.ids?.length) return
    const ids = confirmModal.ids
    setDeleting(true)
    try {
      await Promise.all(ids.map((id) => deleteCorporateClient(id)))
      openSnackbar(`${ids.length} client(s) supprimé(s)`)
      setConfirmModal(null)
      setSelectedIds((prev) => prev.filter((id) => !ids.includes(id)))
      const remainingOnPage = (data?.clients?.length ?? ids.length) - ids.length
      if (remainingOnPage === 0 && page > 1) {
        setPage((p) => p - 1)
      } else {
        load()
      }
    } catch (error) {
      openSnackbar(error?.response?.data?.message || 'Erreur suppression')
    } finally {
      setDeleting(false)
    }
  }

  const requestDeleteClients = (ids) => {
    if (!ids.length) return
    setConfirmModal({ ids })
  }

  const handleImportExcel = async (event) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return

    const formData = new FormData()
    formData.append('file', file)
    setImporting(true)
    try {
      const { data: result } = await importCorporateClientsExcel(formData)
      openSnackbar(result.message || 'Import terminé')
      if (result.errorCount > 0) {
        openSnackbar(`${result.errorCount} ligne(s) ignorée(s) — vérifiez le fichier`)
      }
      load()
    } catch (error) {
      openSnackbar(error?.response?.data?.message || 'Erreur import Excel')
    } finally {
      setImporting(false)
    }
  }

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    )
  }

  if (loading && !data) {
    return (
      <div className={styles.loading}>
        <Spinner />
      </div>
    )
  }

  const { clients = [], pagination, stats } = data || {}
  const total = pagination?.total ?? 0
  const totalPages = pagination?.totalPages ?? 1
  const from = total === 0 ? 0 : (page - 1) * limit + 1
  const to = total === 0 ? 0 : Math.min(page * limit, total)
  const pageIds = clients.map((c) => c._id)
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id))

  const toggleSelectAllOnPage = () => {
    if (allPageSelected) {
      setSelectedIds((prev) => prev.filter((id) => !pageIds.includes(id)))
    } else {
      setSelectedIds((prev) => [...new Set([...prev, ...pageIds])])
    }
  }

  const deleteCount = confirmModal?.ids?.length ?? 0

  return (
    <div className={styles.page}>
      <BackButton onClick={() => history.push('/corporate/dashboard')}>
        Dashboard Corporate
      </BackButton>

      <div className={styles.topBar}>
        <div>
          <h1>Clients convention</h1>
          <p>Référentiel clients Corporate — nom identique au fichier PDF Sage X3</p>
        </div>
        <div className={styles.topBarActions}>
          {canManageClients && (
            <>
              <input
                ref={importInputRef}
                type="file"
                accept=".xlsx,.xls"
                className={styles.hiddenFileInput}
                onChange={handleImportExcel}
              />
              <button
                type="button"
                className={styles.secondaryBtn}
                disabled={importing || loading}
                onClick={() => importInputRef.current?.click()}
              >
                {importing ? 'Import…' : 'Importer Excel'}
              </button>
            </>
          )}
          <button type="button" className={styles.ctaBtn} onClick={openCreate}>
            + Nouveau client
          </button>
        </div>
      </div>

      <div className={styles.kpiGrid}>
        <div className={`${styles.kpiCard} ${styles.kpiPrimary}`}>
          <span className={styles.kpiLabel}>Total clients</span>
          <span className={styles.kpiValue}>{stats?.totalClients ?? 0}</span>
          <span className={styles.kpiSub}>Conventions enregistrées</span>
        </div>
        <div className={`${styles.kpiCard} ${styles.kpiOk}`}>
          <span className={styles.kpiLabel}>Emails configurés</span>
          <span className={styles.kpiValue}>{stats?.withEmail ?? 0}</span>
          <span className={styles.kpiSub}>Prêts pour l&apos;envoi</span>
        </div>
        <div className={`${styles.kpiCard} ${styles.kpiWarn}`}>
          <span className={styles.kpiLabel}>Sans email</span>
          <span className={styles.kpiValue}>{stats?.withoutEmail ?? 0}</span>
          <span className={styles.kpiSub}>À compléter avant envoi</span>
        </div>
        <div className={styles.kpiCard}>
          <span className={styles.kpiLabel}>Envois cumulés</span>
          <span className={styles.kpiValue}>{stats?.totalSends ?? 0}</span>
          <span className={styles.kpiSub}>PDF envoyés par email</span>
        </div>
      </div>

      <div className={styles.filterBar}>
        <div className={styles.filterGroup}>
          <label htmlFor="corp-client-search">Rechercher</label>
          <input
            id="corp-client-search"
            type="search"
            className={styles.searchInput}
            placeholder="Nom, vis-à-vis, matricule fiscal, adresse, email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div className={styles.panel}>
        <div className={styles.panelHeader}>
          <div>
            <h2>Liste des clients</h2>
            <p className={styles.hint}>
              Le nom doit correspondre exactement au fichier PDF exporté depuis Sage X3 (sans .pdf).
              {canManageClients && ' Import Excel : Entreprise, Vis-à-vis, Adresse e-mail, Matricule fiscale, Adresse.'}
            </p>
          </div>
          <div className={styles.panelActions}>
            {total > 0 && (
              <span className={styles.hint}>{total} client(s)</span>
            )}
            {canManageClients && selectedIds.length > 0 && (
              <button
                type="button"
                className={styles.deleteBulkBtn}
                disabled={deleting || loading}
                onClick={() => requestDeleteClients(selectedIds)}
              >
                {deleting ? 'Suppression…' : `Supprimer (${selectedIds.length})`}
              </button>
            )}
          </div>
        </div>

        {clients.length === 0 ? (
          <p className={styles.empty}>Aucun client convention — créez-en un ou importez des PDF.</p>
        ) : (
          <>
            <div className={`${styles.tableWrap} table-scroll-wrap`}>
              <table className={`${styles.table} responsive-table`}>
                <thead>
                  <tr>
                    {canManageClients && (
                      <th className={styles.checkCol}>
                        <input
                          type="checkbox"
                          checked={allPageSelected}
                          onChange={toggleSelectAllOnPage}
                          aria-label="Sélectionner tous les clients de la page"
                        />
                      </th>
                    )}
                    <th>Client</th>
                    <th>Vis-à-vis</th>
                    <th>Matricule fiscal</th>
                    <th>Adresse</th>
                    <th>Email facturation</th>
                    <th>Envois</th>
                    <th className={styles.actionsCol}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {clients.map((c) => (
                    <tr key={c._id}>
                      {canManageClients && (
                        <td className={styles.checkCol} data-label="Sélection">
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(c._id)}
                            onChange={() => toggleSelect(c._id)}
                            aria-label={`Sélectionner ${c.name}`}
                          />
                        </td>
                      )}
                      <td data-label="Client">
                        <div className={styles.clientCell}>
                          <span className={styles.avatar} aria-hidden="true">{clientInitial(c.name)}</span>
                          <div>
                            <button
                              type="button"
                              className={styles.clientLink}
                              onClick={() => history.push(`/corporate/clients/${c._id}`)}
                            >
                              {c.name}
                            </button>
                            {c.lastCampaignPeriod && (
                              <span className={styles.clientHint}>Dernière campagne : {c.lastCampaignPeriod}</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className={styles.visAVisCell} data-label="Vis-à-vis" title={c.visAVis || undefined}>
                        {c.visAVis || '—'}
                      </td>
                      <td data-label="Matricule fiscal">{c.matriculeFiscal || '—'}</td>
                      <td className={styles.addressCell} data-label="Adresse">{c.address || '—'}</td>
                      <td className={styles.emailCell} data-label="Email facturation">
                        {c.email
                          ? <span className={styles.badgeOk} title={c.email}>{c.email}</span>
                          : <span className={styles.badgeErr}>Email manquant</span>}
                      </td>
                      <td data-label="Envois">{c.sendCount || 0}</td>
                      <td className={`${styles.actionsCol} action-cell`} data-label="Actions">
                        <div className={`${styles.actionCell} corp-icon-actions`}>
                          <button
                            type="button"
                            className={`${styles.iconBtn} ${styles.iconBtnView}`}
                            onClick={() => history.push(`/corporate/clients/${c._id}`)}
                            title="Voir détails"
                            aria-label={`Voir détails de ${c.name}`}
                          >
                            <VisibilityOutlinedIcon fontSize="small" />
                          </button>
                          <button
                            type="button"
                            className={`${styles.iconBtn} ${styles.iconBtnEdit}`}
                            onClick={() => openEdit(c)}
                            title="Modifier"
                            aria-label={`Modifier ${c.name}`}
                          >
                            <EditOutlinedIcon fontSize="small" />
                          </button>
                          {canManageClients && (
                            <button
                              type="button"
                              className={`${styles.iconBtn} ${styles.iconBtnDelete}`}
                              onClick={() => requestDeleteClients([c._id])}
                              disabled={deleting}
                              title="Supprimer"
                              aria-label={`Supprimer ${c.name}`}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className={styles.pagination}>
              <div className={styles.paginationLeft}>
                <label className={styles.pageSizeLabel} htmlFor="corp-client-limit">
                  Afficher
                </label>
                <select
                  id="corp-client-limit"
                  className={styles.pageSizeSelect}
                  value={limit}
                  onChange={(e) => setLimit(Number(e.target.value))}
                  disabled={loading}
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
                <span className={styles.pageSizeLabel}>par page</span>
              </div>

              <div className={styles.paginationRight}>
                <span className={styles.pageLabel}>
                  {from}–{to} sur {total}
                </span>
                <button
                  type="button"
                  className={styles.pageBtn}
                  disabled={page <= 1 || loading}
                  onClick={() => setPage((p) => p - 1)}
                >
                  ← Précédent
                </button>
                <span className={styles.pageLabel}>Page {page} / {totalPages}</span>
                <button
                  type="button"
                  className={styles.pageBtn}
                  disabled={page >= totalPages || loading}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Suivant →
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {modalOpen && (
        <div className={styles.overlay} onClick={() => !saving && setModalOpen(false)} role="presentation">
          <div
            className={styles.modal}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="corp-client-modal-title"
          >
            <h2 id="corp-client-modal-title">{editing ? 'Modifier le client' : 'Nouveau client'}</h2>
            <p className={styles.modalHint}>
              {editing
                ? 'Mettez à jour les coordonnées de facturation. Le nom doit rester identique au fichier PDF Sage X3 (sans .pdf) pour que l’import fonctionne.'
                : 'Le nom doit être identique au fichier PDF Sage X3 (sans extension).'}
            </p>
            <form onSubmit={handleSave}>
              <div className={styles.field}>
                <label htmlFor="corp-client-name">Nom (identique PDF Sage) *</label>
                <input
                  id="corp-client-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  placeholder="Ex. STE CALIFORNIA GYM SARL"
                />
              </div>
              <div className={styles.field}>
                <label htmlFor="corp-client-vis">Vis-à-vis</label>
                <input
                  id="corp-client-vis"
                  value={form.visAVis}
                  onChange={(e) => setForm({ ...form, visAVis: e.target.value })}
                  placeholder="Ex. Aymen Gazbour"
                />
              </div>
              <div className={styles.field}>
                <label htmlFor="corp-client-mf">Matricule fiscal</label>
                <input
                  id="corp-client-mf"
                  value={form.matriculeFiscal}
                  onChange={(e) => setForm({ ...form, matriculeFiscal: e.target.value })}
                  placeholder="1234567/A/B/C/000"
                />
              </div>
              <div className={styles.field}>
                <label htmlFor="corp-client-address">Adresse</label>
                <textarea
                  id="corp-client-address"
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  placeholder="Ex. 12 Avenue Habib Bourguiba, Tunis"
                  rows={3}
                />
              </div>
              <div className={styles.field}>
                <label htmlFor="corp-client-email">Email facturation</label>
                <input
                  id="corp-client-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="facturation@client.tn"
                />
              </div>
              <div className={styles.modalActions}>
                <button type="button" className={styles.cancelBtn} onClick={() => setModalOpen(false)} disabled={saving}>
                  Annuler
                </button>
                <button type="submit" className={styles.saveBtn} disabled={saving}>
                  {saving ? 'Enregistrement…' : 'Enregistrer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmModal
        open={!!confirmModal}
        variant="warning"
        title="Supprimer le(s) client(s) ?"
        message={
          deleteCount === 1
            ? 'Ce client sera retiré du référentiel.'
            : `Supprimer ${deleteCount} clients du référentiel ?`
        }
        detail="Les PDF déjà envoyés en campagne restent archivés. Cette action est irréversible."
        confirmLabel="Supprimer"
        onConfirm={handleDelete}
        onCancel={() => !deleting && setConfirmModal(null)}
        loading={deleting}
      />
    </div>
  )
}

export default CorporateClients
